import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation, useQuery, useQueryClient } = ReactQueryV5;
import { Button, Dialog, Icon, Tooltip } from "@mindlercare/ui-web";
import { useMindlerApi } from "api/apiv2";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Typography } from "components/typography";
import { getMedicorePatientLink } from "./getMedicorePatientLink";
import { SharedProps } from "./types";
import { useMedicoreEnabled } from "./useMedicoreEnabled";

export const VerifyPatient = ({ patientUserId }: SharedProps) => {
  const { t } = useTranslation();
  const enabled = useMedicoreEnabled(patientUserId);
  const mindlerApi = useMindlerApi();
  const queryClient = useQueryClient();
  const { data } = useQuery(
    mindlerApi.medicore.getMedicorePatientByPatientUserId.queryOptions(
      {
        patientUserId: Number(patientUserId),
      },
      {
        enabled,
      },
    ),
  );
  const medicorePatientLink = getMedicorePatientLink(data?.medicorePatientId);
  const { mutateAsync } = useMutation(
    mindlerApi.medicore.verifyMedicorePatient.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries(
          mindlerApi.medicore.getMedicorePatientByPatientUserId.queryFilter({
            patientUserId: Number(patientUserId),
          }),
        );
      },
    }),
  );

  if (!enabled) return null;

  const hasMedicorePatientId = !!data?.medicorePatientId;

  return (
    <Dialog
      trigger={
        <Tooltip
          content={t("MEDICORE.PATIENT_CANNOT_BE_FOUND_TOOLTIP")}
          disabled={hasMedicorePatientId}
        >
          <Dialog.Trigger
            variant="primary"
            disabled={!hasMedicorePatientId || data?.verified}
          >
            {t("MEDICORE.VERIFY_PATIENT")}
          </Dialog.Trigger>
        </Tooltip>
      }
    >
      <Dialog.Title>
        <Typography insensitive variant="h3">
          {t("MEDICORE.VERIFY_PATIENT")}
        </Typography>
      </Dialog.Title>
      <Dialog.Content>
        <FlexVertical>
          <Typography insensitive variant="body" color="GRAY_DARK">
            {t("MEDICORE.VERIFY_PATIENT_DESCRIPTION")}
          </Typography>
          <Button
            variant="link-button"
            href={medicorePatientLink}
            target="_blank"
          >
            {t("MEDICORE.OPEN_MEDICORE")}
          </Button>

          <FlexHorizontal>
            <NotVerifiedIcon />
            <Typography insensitive variant="body-small" color="GRAY_DARK">
              {t("MEDICORE.PATIENT_CANNOT_BE_VERIFIED_DISCLAIMER")}
            </Typography>
          </FlexHorizontal>
        </FlexVertical>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.Cancel>{t("CANCEL")}</Dialog.Cancel>
        <Dialog.Confirm
          onClick={async () => {
            await mutateAsync({ patientUserId: Number(patientUserId) });
          }}
        >
          {t("MEDICORE.VERIFY")}
        </Dialog.Confirm>
      </Dialog.Actions>
    </Dialog>
  );
};

const FlexVertical = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${(props) => props.theme.SPACING.LG};
  margin: ${(props) => props.theme.SPACING.LG} 0;
`;

const FlexHorizontal = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${(props) => props.theme.SPACING.MD};
`;

const NotVerifiedIcon = styled(Icon.Verified)`
  color: ${(props) => props.theme.COLORS.ERROR};
`;
